import { useCallback, useMemo } from 'react'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { getSellVariables } from '../../../../../../../utils/reducers/getters'
import { InputTableLeftColumnChange, InputTableLeftTabsProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import InputTableLeftColumnForm from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { twinFetchPostJSON, twinFetchPostText } from '../../../../../../../utils/globals/data'
import TitleAndDeleteOrderHeaderOption from '../../../../../../../specificComponents/TitleAndDeleteOrderHeaderOption'
import { QuoteOrderVariableModelType } from '@teinor/erp/types/company/order/quote/quoteOrder/quoteOrderVariable'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import { SellVariableTypeInversed, SellVariableTypeKeys } from '@teinor/erp/types/company/config/sellVariable'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { flushSync } from 'react-dom'
import { updateVariableQuantityScaled } from '../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalEditAddonProductOrder/functions'
import { recalculateAllQuoteOrderRows } from '@teinor/erp/prices/quote/calculate'
import { OrderHeaderURLs } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'

interface QuoteStepHeaderVariablesProps extends QuoteStepHeaderVariablesLogicProps { }

const QuoteStepHeaderVariables: React.FC<QuoteStepHeaderVariablesProps> = ({ headerData, getCompleteData, orderHeaderURLs, ...logic }) => {
    const { t } = useTwinTranslation()
    const { myHandleChange, dictVariables } = useQuoteStepHeaderVariablesLogic({ headerData, getCompleteData, orderHeaderURLs, ...logic })
    const variables = headerData.OrderVariables
    const currencyHeaderId = headerData.CurrencyId
    const keysDictLength = Object.keys(dictVariables)?.length
    const idsOrderVariables = useMemo(() => variables?.map((item: QuoteOrderVariableModelType) => String(item.OrderVariableId)), [variables])
    const sections: FormRender<any> = []
    if (keysDictLength && keysDictLength !== idsOrderVariables?.length) {
        sections.push({
            cols: 1,
            elements: [
                {
                    name: 'add',
                    component: 'CustomSelectSearchBarWithNotInFilteredItems',
                    label: t('addVariable', 'Añadir variable'),
                    items: dictVariables,
                    onChange: getCompleteData,
                    excludeItems: idsOrderVariables,
                },
            ]
        })
    }
    if (variables) {
        const dictComponents: Record<SellVariableTypeKeys, 'InputNumberWithLabelMarginTextRightDebounce' | 'InputWithLabelMargin' | 'InputPrice'> = {
            0: 'InputNumberWithLabelMarginTextRightDebounce',
            1: 'InputWithLabelMargin',
            2: 'InputPrice',
            3: 'InputWithLabelMargin'
        }
        for (let key in variables) {
            const variable = variables[key]
            let myExtraData: TwinDictionary = {}
            variable.name = dictVariables[variable.OrderVariableId].name
            if (dictVariables?.[variable.OrderVariableId]?.type === SellVariableTypeInversed['selector']) {
                if (!variable.value) {
                    myExtraData.items = dictVariables[variable.OrderVariableId].extraDataParsed
                    myExtraData.component = 'CustomSelectWithSearchBar'
                }
            }
            sections.push({
                title: <TitleAndDeleteOrderHeaderOption fieldValue='OrderVariableId' option={variable} url={orderHeaderURLs.deleteVariable}{...{ getCompleteData }} />,
                cols: 1,
                elements: [
                    {
                        name: variable.OrderVariableId+ '_key_' + key,
                        component: dictComponents?.[dictVariables?.[variable.OrderVariableId]?.type] || 'InputNumberWithLabelMarginTextRightDebounce',
                        label: dictVariables?.[variable.OrderVariableId]?.name,
                        value: variable.value || '',
                        textRight: '',
                        CurrencyId: currencyHeaderId,
                        className: returnClassOfSpanTemplate(4),
                        ...myExtraData
                    },
                ]
            })
        }
    }

    return (<InputTableLeftColumnForm form={sections} handleChange={myHandleChange} headerData={headerData} />
    )
}

export interface QuoteStepHeaderVariablesLogicProps extends InputTableLeftTabsProps {
    orderHeaderURLs: OrderHeaderURLs
}

export const useQuoteStepHeaderVariablesLogic = ({ setCompleteData, orderHeaderURLs }: QuoteStepHeaderVariablesLogicProps) => {
    const dictVariables = useMemo(() => getSellVariables() || {}, [])

    const myHandleAdd: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        await twinFetchPostJSON(orderHeaderURLs.createVariable, {
            OrderId: String(id),
            OrderVariableId: value,
        })
    }, [orderHeaderURLs])

    const myHandleVariable: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        const fieldSplit = field.split('_key_')
        const OrderVariableId = fieldSplit[0]
        const keyVariable = parseInt(fieldSplit[1])
        let relatedProducts: any[] = []
        await flushSync(async () => {
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const copyOld = JSON.parse(JSON.stringify(old))
                if (!copyOld.header.OrderVariables[keyVariable].value && dictVariables?.[OrderVariableId]?.type === SellVariableTypeInversed['selector']) {
                    value = dictVariables?.[OrderVariableId]?.extraDataParsed?.[value]?.name
                }
                copyOld.header.OrderVariables[keyVariable] = {
                    ...copyOld.header.OrderVariables[keyVariable], value: value,
                }
                relatedProducts = updateVariableQuantityScaled(copyOld, OrderVariableId, value)
                if (relatedProducts?.length) {
                    recalculateAllQuoteOrderRows(copyOld)
                }
                return copyOld
            })
            await twinFetchPostText(orderHeaderURLs.updateVariable, {
                OrderId: id,
                OrderVariableId,
                value: value,
                relatedProducts
            })
        })
    }, [setCompleteData, orderHeaderURLs, dictVariables])

    const myHandleChange: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        if (field === 'add') {
            await myHandleAdd(id, field, value)
        } else {
            await myHandleVariable(id, field, value)
        }
    }, [myHandleAdd, myHandleVariable])

    return { myHandleAdd, myHandleChange, dictVariables }
}

export default QuoteStepHeaderVariables